import { useCallback, useEffect, useState } from 'react'
import { TextField as MuiTextField, TextFieldProps as MuiTextFieldProps } from '@mui/material'
import { useDebounceCallback } from '@global/hooks/useDebounceCallback'

interface TextFieldProps extends Omit<MuiTextFieldProps, 'value' | 'onChange'> {
  value: string
  setValueCallback: (value: string) => void
}

/**
 * A TextField component that debounces input value changes.
 *
 * This component wraps the Material-UI TextField component, adding debouncing to the process of
 * updating the value. This is particularly useful for reducing the frequency of value change
 * callbacks being triggered, which can improve performance and reduce unnecessary processing,
 * especially in scenarios involving real-time validation or API calls based on the input value.
 *
 * @param {TextFieldProps} props - The props for the TextField component.
 * @param {string} props.value - The current value of the text field, used for controlled input.
 * @param {(value: string) => void} props.setValueCallback - The callback function to be called with the debounced value of the text field.
 * This function should handle the updated value, for example, by setting state in a parent component or making an API call.
 * @param {MuiTextFieldProps} props.props - Additional props to be spread onto the Material-UI TextField component. This can include
 * any valid props for MuiTextField such as `variant`, `label`, `type`, etc.
 *
 * @returns A Material-UI TextField component with debounced value change handling.
 */
export const TextField = ({ value, setValueCallback, ...props }: TextFieldProps) => {
  const [val, setVal] = useState(value)

  // Keep a parent value in sync with the debounced value.
  useEffect(() => {
    setVal(value)
  }, [value])

  /**
   * Optimizing the callback in this way allows for a value that changes frequently,
   * without causing the debounced function to be recreated on every render, while still
   * ensuring that the debounced function has access to the latest value. That's the magic
   * of useDebounceCallback.
   */
  const debounceCallback = useDebounceCallback({
    callback: () => {
      setValueCallback(val || '')
    },
  })

  // Created on every render due to the state change, but the debounced function is optimized.
  const onChange = useCallback(function onChange(e) {
    const v = e.target.value
    setVal(v)
    debounceCallback()
  }, [])

  return <MuiTextField variant="standard" value={val} onChange={onChange} {...props} />
}
